/**
 * Tâche 1443 (Eric 2026-10-02) — Résultats du balayage, groupés par variante.
 *
 * Deux choix d'Eric portés par cet écran :
 *   • le balayage **détecte** et ne modifie rien ;
 *   • on peut **corriger au fur et à mesure**, une occurrence à la fois, sans
 *     devoir aller la chercher ailleurs dans l'interface.
 *
 * Tâche 1455 — L'état de chaque occurrence (acceptée / refusée) vient du
 * SERVEUR (`balayage.resolutions`) et non de l'état de l'écran : un
 * rechargement de page effaçait sinon tout le travail de tri.
 *
 * Tâche 1456 — **Affichage dense, deux lignes par occurrence** :
 *   ligne 1 : la source (Whisper · Fireflies · Fusionnée · Q&R…), le contexte,
 *             « Corriger ici », « Voir l'endroit », « Refuser » ;
 *   ligne 2 : l'extrait centré sur le mot fautif.
 * Le chemin technique (« full_text ») a été RETIRÉ : il mangeait une ligne par
 * occurrence pour une information qui n'aide personne à décider.
 *
 * Tâche 1457 — « Accepter ce terme partout » est posé à droite du terme
 * corrigé, dans l'en-tête du groupe : c'est là qu'on lit « l'ABF → la BF » et
 * donc là qu'on décide que la correction vaut pour toutes les occurrences.
 */
import { useMemo, useState } from "react";
import {
  ArrowSquareOut, ArrowUUpLeft, CaretDown, CaretRight, Check, Lightning,
  Prohibit, SpinnerGap, Warning,
} from "@phosphor-icons/react";
import { confirmDialog } from "../../lib/uiDialogs";
import { useLang } from "../../contexts/LanguageContext";

const Extrait = ({ occ }) => (
  <span className="text-xs text-zinc-600 break-words">
    {occ.extrait.slice(0, occ.extrait_debut)}
    <mark className="bg-rose-100 text-rose-900 font-semibold rounded px-0.5">
      {occ.extrait.slice(occ.extrait_debut, occ.extrait_fin)}
    </mark>
    {occ.extrait.slice(occ.extrait_fin)}
  </span>
);

export const BalayageResultats = ({
  balayage, libellesZone, onCorriger, onRefuser, onReprendre,
  onAccepterTerme, zonesFiltre = [],
}) => {
  const { lang, tr } = useLang();
  const [etats, setEtats] = useState({});
  const [ouverts, setOuverts] = useState({});
  const [enMasse, setEnMasse] = useState("");

  const resolutions = balayage?.resolutions || {};

  const groupes = useMemo(() => {
    const parVariante = {};
    (balayage?.occurrences || []).forEach((occ) => {
      if (zonesFiltre.length && !zonesFiltre.includes(occ.zone)) return;
      const cle = `${occ.variante}→${occ.terme}`;
      if (!parVariante[cle]) {
        parVariante[cle] = { cle, variante: occ.variante, terme: occ.terme, occ: [] };
      }
      parVariante[cle].occ.push(occ);
    });
    return Object.values(parVariante).sort((a, b) => b.occ.length - a.occ.length);
  }, [balayage, zonesFiltre]);

  const corriger = async (occ) => {
    setEtats((e) => ({ ...e, [occ.occ_id]: { statut: "en_cours" } }));
    const resultat = await onCorriger(occ);
    setEtats((e) => ({ ...e, [occ.occ_id]: resultat }));
  };

  const annuler = async (occ, correctionId) => {
    setEtats((e) => ({ ...e, [occ.occ_id]: { statut: "en_cours" } }));
    const resultat = await onCorriger(occ, correctionId);
    setEtats((e) => ({ ...e, [occ.occ_id]: resultat }));
  };

  const refuser = async (occ) => {
    setEtats((e) => ({ ...e, [occ.occ_id]: { statut: "en_cours" } }));
    await onRefuser(occ);
    setEtats((e) => ({ ...e, [occ.occ_id]: {} }));
  };

  const reprendre = async (occ) => {
    setEtats((e) => ({ ...e, [occ.occ_id]: { statut: "en_cours" } }));
    await onReprendre(occ);
    setEtats((e) => ({ ...e, [occ.occ_id]: {} }));
  };

  if (!balayage) return null;

  const zoneLabel = (zone) => {
    const paire = libellesZone?.[zone];
    if (!paire) return zone;
    return lang === "en" ? paire[1] : paire[0];
  };

  /** Tâche 1457 — toutes les occurrences d'UNE variante, d'un seul geste. */
  const accepterTerme = async (g) => {
    const aFaire = g.occ.filter((o) => !resolutions[o.occ_id]).length;
    if (!aFaire) return;
    const ok = await confirmDialog(
      tr(`Remplacer « ${g.variante} » par « ${g.terme} » dans les ${aFaire} endroit(s) restants ?\n\nLe texte est réécrit immédiatement. Chaque correction reste annulable une par une.`,
         `Replace "${g.variante}" with "${g.terme}" in the ${aFaire} remaining place(s)?\n\nThe text is rewritten immediately. Each fix stays undoable one by one.`),
      {
        tone: "destructive",
        confirmLabel: tr(`Oui, corriger les ${aFaire} endroits`, `Yes, fix the ${aFaire} places`),
        cancelLabel: tr("Annuler", "Cancel"),
      });
    if (!ok) return;
    setEnMasse(g.cle);
    try {
      await onAccepterTerme(g.variante);
    } finally {
      setEnMasse("");
    }
  };

  const restantes = (balayage.occurrences || []).filter((o) => !resolutions[o.occ_id]).length;

  return (
    <div data-testid="orthographe-resultats">
      <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-zinc-600">
        <span className="font-semibold text-zinc-900" data-testid="orthographe-resultats-total">
          {balayage.nb_occurrences} {tr("occurrence(s) trouvée(s)",
            balayage.nb_occurrences === 1 ? "occurrence found" : "occurrences found")}
        </span>
        {/* Tâche 1455 — ce compte est la raison pour laquelle le balayage reste
            affiché : tant qu'il n'est pas à zéro, il y a du travail en cours. */}
        <span className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-900 font-medium"
              data-testid="orthographe-resultats-restantes">
          {restantes} {tr("à trier", "to sort")}
        </span>
        <span>
          {tr("sur", "over")} {balayage.docs_lus} {tr("document(s) lu(s)", "document(s) read")}
        </span>
        <span>
          {balayage.nb_variantes} {tr("variante(s) recherchée(s)", "variant(s) searched")}
        </span>
      </div>

      {balayage.tronque && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2"
             data-testid="orthographe-tronque">
          <Warning size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900">
            {tr("Ce balayage est TRONQUÉ : la base dépasse la borne de lecture. Le compte affiché est donc un minimum, pas un total. Corrigez puis relancez pour voir la suite.",
                "This scan is TRUNCATED: the database exceeds the read limit. The count shown is a minimum, not a total. Fix some, then run it again to see the rest.")}
          </p>
        </div>
      )}

      {groupes.length === 0 && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center"
             data-testid="orthographe-aucune-occurrence">
          <Check size={22} className="text-emerald-600 mx-auto mb-2" />
          <p className="text-sm font-medium text-emerald-900">
            {zonesFiltre.length
              ? tr("Aucune faute dans les catégories choisies.",
                   "No mistake in the chosen categories.")
              : tr("Aucune de ces fautes n'est présente dans la base.",
                   "None of these mistakes is present in the database.")}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {groupes.map((g) => {
          // Tâche 1486 (Eric) — REPLIÉ par défaut : un balayage de 200
          // occurrences déployait 200 lignes d'un coup, et la liste des
          // termes détectés devenait introuvable.
          const ouvert = ouverts[g.cle] === true;
          const aTrier = g.occ.filter((o) => !resolutions[o.occ_id]).length;
          return (
            <div key={g.cle}
                 /* Tâche 1490 (Eric) — Bandeau GRIS quand plus rien n'est à
                    trier : retirer le bouton disait « il n'y a plus d'action
                    possible », pas « c'est fait ». La couleur le dit d'un
                    coup d'œil sur une liste de vingt termes. */
                 className={`border rounded-xl overflow-hidden ${
                   aTrier === 0
                     ? "border-zinc-300 bg-zinc-100"
                     : "border-zinc-200 bg-white"
                 }`}
                 data-testid={`orthographe-groupe-${g.variante}`}
                 data-traite={aTrier === 0 ? "oui" : "non"}>
              {/* Tâche 1456 — ⚠️ `flex-wrap` et `w-full` sur mobile : sans
                  eux, le bouton « Accepter ce terme partout » écrasait la
                  colonne du terme à 390 px et « Zzorthoessai » se cassait
                  UNE LETTRE PAR LIGNE (vu à l'écran). Un nom métier long
                  (« CELIAPP », « ABF ») subirait exactement le même sort. */}
              <div className={`flex flex-wrap items-center gap-2 px-4 py-2.5 transition-colors ${
                aTrier === 0 ? "hover:bg-zinc-200" : "hover:bg-zinc-50"
              }`}>
                <button
                  type="button"
                  onClick={() => setOuverts((o) => ({ ...o, [g.cle]: !ouvert }))}
                  className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:flex-1 min-w-0 text-left"
                  data-testid={`orthographe-groupe-toggle-${g.variante}`}
                >
                  {/* Tâche 1486 — Le triangle DIT qu'on peut replier. Sans
                      lui, la ligne était cliquable mais personne ne le
                      devinait. */}
                  {ouvert
                    ? <CaretDown size={14} weight="bold" className="text-zinc-500 flex-shrink-0"
                                 data-testid={`orthographe-groupe-ouvert-${g.variante}`} />
                    : <CaretRight size={14} weight="bold" className="text-zinc-500 flex-shrink-0"
                                  data-testid={`orthographe-groupe-ferme-${g.variante}`} />}
                  <span className="text-sm text-zinc-800 min-w-0">
                    <span className="font-semibold text-rose-700 break-words">{g.variante}</span>
                    <span className="text-zinc-400 mx-2">→</span>
                    <span className="font-semibold text-emerald-700 break-words">{g.terme}</span>
                  </span>
                  <span className="text-[11px] text-zinc-500 whitespace-nowrap flex-shrink-0">
                    {g.occ.length} {tr("endroit(s)", g.occ.length === 1 ? "place" : "places")}
                    {aTrier !== g.occ.length && (
                      <span className="text-emerald-700 ml-1">
                        — {g.occ.length - aTrier} {tr("traité(s)", "handled")}
                      </span>
                    )}
                  </span>
                </button>
                {aTrier > 0 && (
                  <button
                    type="button"
                    onClick={() => accepterTerme(g)}
                    disabled={enMasse === g.cle}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-rose-600 text-white text-[11px] font-medium hover:bg-rose-700 transition-colors disabled:opacity-40 flex-shrink-0"
                    data-testid={`orthographe-accepter-terme-${g.variante}`}
                  >
                    {enMasse === g.cle
                      ? <SpinnerGap size={12} className="animate-spin" />
                      : <Lightning size={12} weight="fill" />}
                    {tr("Accepter ce terme partout", "Accept this term everywhere")}
                  </button>
                )}
              </div>

              {ouvert && (
                <div className="border-t border-zinc-100 divide-y divide-zinc-100">
                  {g.occ.map((occ) => {
                    const etat = etats[occ.occ_id] || {};
                    const resolu = resolutions[occ.occ_id];
                    const corrige = etat.statut === "fait" || resolu?.etat === "corrige";
                    const refuse = resolu?.etat === "refuse";
                    return (
                      <div key={occ.occ_id}
                           className={`px-4 py-2 ${refuse ? "bg-zinc-50" : ""}`}
                           data-testid={`orthographe-occurrence-${occ.occ_id}`}>
                        {/* Tâche 1456 — tout sur UNE ligne : source, contexte,
                            actions. L'extrait vient seul en dessous. */}
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200 flex-shrink-0"
                                data-testid={`orthographe-zone-${occ.occ_id}`}>
                            {zoneLabel(occ.zone)}
                          </span>
                          {occ.titre_contexte && (
                            <span className="text-[11px] text-zinc-500 truncate max-w-[220px]">
                              {occ.titre_contexte}
                            </span>
                          )}

                          {corrige ? (
                            <>
                              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700"
                                    data-testid={`orthographe-corrige-${occ.occ_id}`}>
                                <Check size={13} />
                                {tr("Corrigé", "Fixed")}
                                {etat.restant > 0 && (
                                  <span className="text-amber-700 ml-1">
                                    {tr("— il reste", "— still")} {etat.restant} {tr("dans ce champ", "in this field")}
                                  </span>
                                )}
                              </span>
                              {(etat.correction_id || resolu?.correction_id) && (
                                <button
                                  type="button"
                                  onClick={() => annuler(occ, etat.correction_id || resolu?.correction_id)}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-zinc-300 text-[11px] text-zinc-700 hover:bg-zinc-50 transition-colors"
                                  data-testid={`orthographe-annuler-${occ.occ_id}`}
                                >
                                  <ArrowUUpLeft size={12} />
                                  {tr("Annuler", "Undo")}
                                </button>
                              )}
                            </>
                          ) : refuse ? (
                            <>
                              <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500"
                                    data-testid={`orthographe-refuse-${occ.occ_id}`}>
                                <Prohibit size={13} />
                                {tr("Refusé — ce n'est pas une faute ici.",
                                    "Refused — not a mistake here.")}
                              </span>
                              <button
                                type="button"
                                onClick={() => reprendre(occ)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-zinc-300 text-[11px] text-zinc-700 hover:bg-zinc-50 transition-colors"
                                data-testid={`orthographe-reprendre-${occ.occ_id}`}
                              >
                                <ArrowUUpLeft size={12} />
                                {tr("Remettre à trier", "Put back to sort")}
                              </button>
                            </>
                          ) : etat.statut === "annule" ? (
                            <span className="text-[11px] text-zinc-500"
                                  data-testid={`orthographe-annule-${occ.occ_id}`}>
                              {tr("Correction annulée — le texte d'origine est revenu.",
                                  "Fix undone — the original text is back.")}
                            </span>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => corriger(occ)}
                                disabled={etat.statut === "en_cours"}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#002FA7] text-white text-[11px] font-medium hover:bg-[#001f7a] transition-colors disabled:opacity-50"
                                data-testid={`orthographe-corriger-${occ.occ_id}`}
                              >
                                {etat.statut === "en_cours"
                                  ? <SpinnerGap size={12} className="animate-spin" />
                                  : <Check size={12} />}
                                {tr("Corriger ici", "Fix here")}
                              </button>
                              {occ.lien && (
                                <a
                                  href={occ.lien}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline"
                                  data-testid={`orthographe-lien-${occ.occ_id}`}
                                >
                                  <ArrowSquareOut size={12} />
                                  {tr("Voir l'endroit", "Open the place")}
                                </a>
                              )}
                              {/* Tâche 1455 — « refusé » est un état à part
                                  entière : sans lui, une occurrence qui n'est
                                  PAS une faute resterait éternellement « à
                                  trier » et le balayage ne finirait jamais. */}
                              <button
                                type="button"
                                onClick={() => refuser(occ)}
                                disabled={etat.statut === "en_cours"}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-zinc-300 text-[11px] text-zinc-600 hover:bg-zinc-50 transition-colors disabled:opacity-50"
                                data-testid={`orthographe-refuser-${occ.occ_id}`}
                              >
                                <Prohibit size={12} />
                                {tr("Refuser", "Refuse")}
                              </button>
                            </>
                          )}
                          {etat.statut === "erreur" && (
                            <span className="text-[11px] text-rose-700"
                                  data-testid={`orthographe-erreur-${occ.occ_id}`}>
                              {etat.message}
                            </span>
                          )}
                        </div>
                        <div className="mt-1">
                          <Extrait occ={occ} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BalayageResultats;
